import Vuex from "vuex";
import Vue from "vue";
import keviApi from "./keviApi";
import notificationStore from "./components/notifications/notifications.store";
import { getCurrencySymbol } from "./helpers/currency";

Vue.use(Vuex);

const store = new Vuex.Store({
    strict: false,
    plugins: [],
    state: {
        language: "ptBr",
        logged: false,
        previousRoute: [],
        keviToken: null,
        user: {
            global_admin: false,
            id: null,
            name: null,
            token: null,
            branch_title: null,
            permissions: [],
            modules: [],
            points_program_active: false,
            plugins: [],
        },
        place: {
            id: null,
            name: null,
            uses_activator_flow: null,
            slug: null,
            color: null,
            logo: null,
            active: null,
            currency: "BRL",
            locale: "pt-BR",
            country_code: "BR",
            timezone: "America/Sao_Paulo",
        },
        users: [],
        places: [],
        money: {
            decimal: ",",
            thousands: ".",
            prefix: "R$ ",
            suffix: "",
            precision: 2,
            masked: false /* doesn't work with directive */,
        },
        showOtherPlacesData: false, //If other places data should be shown to global admin
        screen: {
            width: window.innerWidth,
            isMobile: false
        }
    },
    actions: {
        checkIfUserIsLogged: function ({dispatch}) {
            let currentUser = window.localStorage.getItem("user");
            if (currentUser) {
                currentUser = JSON.parse(currentUser);
                dispatch("setUser", currentUser);
            } else {
                dispatch("logout");
            }
        },
        updateUserInfo: function ({commit, dispatch}) {
            dispatch("updateKeviToken");
            let options = keviApi.getOptions("auth/me", "GET");
            Vue.http(options).then(function (response) {
                if (response.status === 200) {
                    dispatch("loadUsers");
                    let currentUser = JSON.parse(
                        window.localStorage.getItem("user"),
                    );
                    if (currentUser) {
                        let userData = Object.assign(
                            currentUser,
                            response.data,
                        );
                        window.localStorage.setItem(
                            "user",
                            JSON.stringify(userData),
                        );
                    }
                    commit("setUser", response.body);
                }
            });
        },
        loadUsers({commit}) {
            let options = keviApi.getOptions("place/users");
            Vue.http(options).then((response) => {
                commit("setUsers", response.body);
            });
        },
        setUser: function ({commit}, user) {
            let lastUser = JSON.parse(localStorage.getItem("user")) || null;
            if (window.mixpanel && lastUser?.users_id != user.users_id) {
                window.mixpanel.identify(user.users_id);
                window.mixpanel.people.set(user);

                //modules
                window.mixpanel.people.set(
                    "enabled_modules",
                    user.modules
                        .map((m) => (m.enabled ? m.module : null))
                        .filter((m) => m != null),
                );
                window.mixpanel.people.set(
                    "enabled_modules_objects",
                    user.modules
                        .map((m) =>
                            m.enabled ? {module: m.module, id: m.id} : null,
                        )
                        .filter((m) => m != null),
                );
                window.mixpanel.people.set(
                    "enabled_modules_ids",
                    user.modules
                        .map((m) => (m.enabled ? m.id : null))
                        .filter((m) => m != null),
                );

                //permissions
                window.mixpanel.people.set(
                    "enabled_permissions",
                    Object.keys(user.permissions)
                        .map((k) => (user.permissions[k].enabled ? k : null))
                        .filter((k) => k != null),
                );
                window.mixpanel.people.set(
                    "enabled_global_access",
                    Object.keys(user.permissions)
                        .map((k) =>
                            user.permissions[k].enabled &&
                            user.permissions[k].options.global_access
                                ? k
                                : null,
                        )
                        .filter((k) => k != null),
                );

                window.mixpanel.track("Sign up", user);
            }
            commit("loggedIn", user);
        },
        hasPermission({state}, permission) {
            if (state.user.id) {
                return state.user.permissions[permission].enabled;
            } else {
                return false;
            }
        },
        logout: function ({commit, state}) {
            if (state.user && state.user.token) {
                // Revoga o token no servidor; o logout local não espera nem depende disso.
                Vue.http(keviApi.getOptions("auth/logout", "POST")).catch(() => {});
            }
            if (window.mixpanel) {
                window.mixpanel.track("Sign down", state.user);
                window.mixpanel.reset();
            }
            window.localStorage.removeItem("user");
            commit("loggedOut");
        },
        login: function ({dispatch}, user) {
            dispatch("setUser", user);
            window.localStorage.setItem("user", JSON.stringify(user));
            dispatch("updateKeviToken");
        },
        changePlace: function ({dispatch, state}, placeId) {
            let options = keviApi.getOptions("auth/change-place", "POST", {
                places_id: placeId,
            });
            if (state.user.global_admin) {
                Vue.http(options).then(function (response) {
                    if (response.status === 200) {
                        dispatch("login", response.body);
                        dispatch("updateKeviToken")
                    }
                });
            }
        },
        updateKeviToken: function ({commit}) {
            const options = keviApi.getOptions('/me/kevi-token');
            Vue.http(options).then(
                (response) => {
                    commit('setKeviToken', response.data.token);
                }
            );
        }
    },
    mutations: {
        setKeviToken(state, token) {
            state.keviToken = token;
        },
        setUsers(state, users) {
            state.users = users;
        },
        setShowOtherPlacesData(state, show) {
            state.showOtherPlacesData = show;
        },
        setPreviousRoute(state, route) {
            state.previousRoute = [route];
        },
        addPreviousRoute(state, route) {
          state.previousRoute = [...state.previousRoute, route];
        },
        setScreen(state, payload) {
            state.screen = payload;
        },
        loggedOut(state) {
            state.logged = false;
            state.user = {
                global_admin: false,
                id: null,
                name: null,
                token: null,
                permissions: [],
                points_program_active: false,
            };
            state.place = {
                id: null,
                name: null,
                uses_activator_flow: null,
                slug: null,
                color: null,
                logo: null,
                active: null,
                branches: [],
            };
        },
        setUser(state, user) {
            state.user = user;
        },
        loggedIn(state, userInfo) {
            let user = {
                global_admin: userInfo.global_admin,
                id: userInfo.users_id,
                name: userInfo.name,
                branch_title: userInfo.branch_title,
                token: userInfo.token,
                permissions: userInfo.permissions,
                modules: userInfo.modules,
                points_program_active: !!userInfo.points_program_active,
                plugins: userInfo.plugins ? userInfo.plugins : [],
            };
            let place = {
                id: userInfo.places_id,
                name: userInfo.place,
                logo: userInfo.logo,
                color: userInfo.color,
                slug: userInfo.slug,
                uses_activator_flow: userInfo.place_uses_activator_flow,
                active: userInfo.place_active,
                branches: userInfo.branches,
                currency: userInfo.place_currency || "BRL",
                locale: userInfo.place_locale || "pt-BR",
                country_code: userInfo.place_country_code || "BR",
                timezone: userInfo.place_timezone || "America/Sao_Paulo",
            };
            state.user = user;
            state.place = place;
            state.money = {
                ...state.money,
                prefix: getCurrencySymbol(place.currency, place.locale) + " ",
            };
            state.logged = true;
        },
        setPlaces(state, places) {
            state.places = places;
        },
    },
    getters: {
        pointsProgram: (state) => () => {
            return state.user.points_program_active === true;
        },
        module: (state) => (module, condition) => {
            if (!state.user.modules) {
                return false;
            }
            const foundModule = state.user.modules.find(
                (m) => m.module === module,
            );
            if (!foundModule) {
                return false;
            }
            if (!foundModule.options) {
                foundModule.options = {};
            }
            if (foundModule && parseInt(foundModule.enabled)) {
                return condition
                    ? condition(foundModule.options)
                    : foundModule.options;
            }
            return false;
        },
        plugin: (state) => (plugin) => {
            return state.user.plugins && state.user.plugins.includes(plugin);
        },
    },
});

store.dispatch("checkIfUserIsLogged");

import debounce from "./helpers/debounce";
import {getData, init} from "./keviClientSDK";

window.addEventListener(
    "storage",
    debounce(
        () => {
            store.dispatch("checkIfUserIsLogged");
        },
        1000,
        true,
    ),
);

store.registerModule("notification", notificationStore);

export default store;
